import React from "react";
import Icon from "components/icon";
import Typography from "components/typography";
import { Table, Columns, TableSortState } from "components/table";
import Pagination from "components/table/pagination";

interface DetailTabsSectionProps {
  activeTab: number;
  setActiveTab: (index: number) => void;
  tabs: any[];
  woHistoryColumns: Columns<any>[];
  woHistory: any[];
  isWoHistoryLoading?: boolean;
  isWoHistoryFetching?: boolean;
  woSortState: TableSortState;
  setWoSortState: (sort: TableSortState) => void;
  woCurrentPage: number;
  woRowsPerPage: number;
  woTotalPages: number;
  setWoCurrentPage: (page: number) => void;
  setWoRowsPerPage: (rows: number) => void;
  documentColumns: Columns<any>[];
  documents: any[];
  t: any;
}

const DetailTabsSection: React.FC<DetailTabsSectionProps> = ({
  activeTab,
  setActiveTab,
  tabs,
  woHistoryColumns,
  woHistory,
  isWoHistoryLoading,
  isWoHistoryFetching,
  woSortState,
  setWoSortState,
  woCurrentPage,
  woRowsPerPage,
  woTotalPages,
  setWoCurrentPage,
  setWoRowsPerPage,
  documentColumns,
  documents,
  t,
}) => {
  return (
    <div className="flex-1 flex flex-col min-h-screen">
      <div className="bg-white px-6">
        <div className="flex border-b border-border w-fit">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(index)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-3 px-8 py-5 text-sm font-bold transition-all border-b-[3px] -mb-[1px] ${
                activeTab === index
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              <Icon 
                name={tab.icon} 
                size={20} 
                className={activeTab === index ? "text-primary-600" : "text-slate-400"} 
              />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-whiteScale-95 p-8">
        <div className="min-h-[500px]">
          {activeTab === 0 && (
            <section className={`rounded-2xl border border-border bg-white shadow-sm overflow-hidden transition-opacity duration-200 ${isWoHistoryFetching && !isWoHistoryLoading ? "opacity-60 pointer-events-none" : ""}`}>
              <Table
                columns={woHistoryColumns}
                data={woHistory}
                loading={isWoHistoryLoading}
                enableColumnDrag={false}
                currentPage={woCurrentPage}
                limit={woRowsPerPage}
                sortState={woSortState}
                onSortStateChange={setWoSortState}
                emptyTitle={t("no-to-history")}
                emptyDescription={t("no-to-history-subtitle")}
                data-testid="table-fleet-to-history"
              />

              <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <Typography variant="body" tone="muted" as="span">
                    {t("rows-per-page")}
                  </Typography>
                  <select
                    value={woRowsPerPage}
                    onChange={(event) => {
                      setWoRowsPerPage(Number(event.target.value));
                      setWoCurrentPage(1);
                    }}
                    className="border border-border rounded-lg px-2 py-1 text-sm bg-white cursor-pointer"
                    data-testid="select-to-history-rows-per-page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div data-testid="pagination-to-history">
                  <Pagination
                    currentPage={woCurrentPage}
                    totalPages={woTotalPages}
                    onPageChange={setWoCurrentPage}
                  />
                </div>
              </div>
            </section>
          )}
          {activeTab === 1 && (
            <Table
              columns={documentColumns}
              data={documents}
              enableColumnDrag={false}
              limit={10}
              emptyTitle={t("tab-fleet-documents")}
              emptyDescription={t("no-to-history-subtitle")} // Fallback or separate key
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailTabsSection;
